import React from "react"
import { useQueryWithEndpoint } from "../hooks/useGraphQLClient"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { DataConnectionState } from "./constants"
import {
  type DataConnectionAsset,
  type ConnectionOwnership,
  getAssetAssignedSystem,
  isHybridOrParentAsset,
  resolveDataConnectionState,
  resolveStateFromConnections,
  getLatestChildActivityTimestamp,
  formatConnectionRelativeTime,
} from "./dataConnectionIndicatorUtils"

export interface UseDataConnectionStatusOptions {
  /** Explicit state override; if provided, bypasses auto-derivation */
  state?: DataConnectionState
  /** Asset entity used to derive state, system ownership, and target navigation URL */
  asset?: DataConnectionAsset | null
  /** Explicit connection presence flag (e.g. from monitoringStatusData) */
  hasConnection?: boolean | null
  /** Optional last data timestamp string or Date */
  lastDataTimestamp?: string | Date | null
  /** Optional system ID override if asset is in a system */
  systemId?: string | null
  /** Optional system name override */
  systemName?: string | null
  /** Custom URL override for setup */
  setupUrl?: string
  /** Explicit loading override */
  loading?: boolean
}

export interface DataConnectionStatusResult {
  state: DataConnectionState
  ownership: ConnectionOwnership
  timestamp: string | Date | null
  isLoading: boolean
  targetUrl: string
  isManagedBySystem: boolean
  systemId: string | null
  systemName: string | null
  relativeTimeLong: string | null
  relativeTimeShort: string | null
}

/**
  * Deep headless hook encapsulating data connection status, precedence, and ownership derivation.
  *
  * Rules & Precedence:
  * 1. If an asset is assigned to a System (via systemId prop, asset.assignments, or systemsList query),
  *    the System's connection takes precedence and ownership is 'system'.
  * 2. If an asset is a hybrid/parent asset, it is Active if at least one child module is active.
  * 3. An unconfigured asset in a system resolves to NotConfigured and routes to the system's connections tab.
  * 4. An unconfigured standalone asset routes to the asset's settings tab.
  */
export function useDataConnectionStatus({
  state: stateProp,
  asset,
  hasConnection,
  lastDataTimestamp,
  systemId: systemIdProp,
  systemName: systemNameProp,
  setupUrl,
  loading: loadingProp,
}: UseDataConnectionStatusOptions = {}): DataConnectionStatusResult {
  // 1. Resolve system from direct asset props / assignments
  const assignedSystem = getAssetAssignedSystem(asset, systemIdProp, systemNameProp)

  // 2. If system was not found directly on asset entity (e.g. in table views where AssetListingFragment
  //    does not include assignments), query systems list to find if this asset belongs to any system.
  const hasAssignmentsLoaded = Boolean(
    (asset as any)?.assignments?.all !== undefined ||
    (asset as any)?.activeAssignment !== undefined
  )

  // TODO: Blocked on core change — see hydrologiq/core#XX:
  // GetSystemsList returns nested assets with class: null because core's
  // _system_list_item_to_graphql omits class_field. Cache poisoning is prevented
  // via Apollo typePolicies in DualGraphQLProvider.tsx until core is updated.
  const shouldFetchSystems =
    stateProp === undefined &&
    hasConnection === undefined &&
    !assignedSystem?.id &&
    !hasAssignmentsLoaded &&
    Boolean(asset?.id)

  const { data: systemsData, loading: systemsLoading } = useQueryWithEndpoint<
    gqlTypes.GetSystemsListQuery,
    gqlTypes.GetSystemsListQueryVariables
  >(
    gqlTypes.GetSystemsListDocument,
    {
      variables: { perPage: 100 },
      skip: !shouldFetchSystems,
      fetchPolicy: "cache-first",
    },
    "primary",
  )

  const matchedSystemFromList = React.useMemo(() => {
    if (!shouldFetchSystems || !asset?.id || !systemsData?.systemsList?.data) return null
    return (
      systemsData.systemsList.data.find((sys) =>
        sys.assets?.some((a) => a.id === asset.id)
      ) ?? null
    )
  }, [shouldFetchSystems, asset?.id, systemsData?.systemsList?.data])

  const effectiveSystemId = assignedSystem?.id ?? matchedSystemFromList?.id ?? null
  const effectiveSystemName = assignedSystem?.name ?? matchedSystemFromList?.name ?? null
  const isManagedBySystem = Boolean(effectiveSystemId)

  // 3. For hybrid/parent assets without children loaded, fetch them to evaluate child states
  const isHybrid = isHybridOrParentAsset(asset)
  const isParent = Boolean(asset?.isParent || isHybrid)

  const shouldFetchChildren =
    stateProp === undefined &&
    isHybrid &&
    Boolean(asset?.id) &&
    (!asset?.children || asset.children.length === 0)

  const { data: childrenData, loading: childrenLoading } = useQueryWithEndpoint<
    gqlTypes.GetAssetChildrenQuery,
    gqlTypes.GetAssetChildrenQueryVariables
  >(
    gqlTypes.GetAssetChildrenDocument,
    {
      variables: { parentId: asset?.id || "" },
      skip: !shouldFetchChildren,
      fetchPolicy: "cache-first",
    },
    "primary",
  )

  const effectiveChildren = React.useMemo(() => {
    if (asset?.children && asset.children.length > 0) return asset.children
    if (childrenData?.getAssetChildren && childrenData.getAssetChildren.length > 0) {
      return childrenData.getAssetChildren
    }
    return asset?.children ?? []
  }, [asset?.children, childrenData?.getAssetChildren])

  const effectiveAsset = React.useMemo(() => {
    if (!asset) return asset
    if (asset.children && asset.children.length > 0) return asset
    if (effectiveChildren && effectiveChildren.length > 0) {
      return { ...asset, children: effectiveChildren }
    }
    return asset
  }, [asset, effectiveChildren])

  // 4. Derive effective telemetry timestamp
  const timestamp = React.useMemo(() => {
    if (lastDataTimestamp) return lastDataTimestamp
    const directTimestamp =
      asset?.current_level_updated_at ??
      asset?.latest_activity?.latestEvent?.createdAt ??
      null
    if (directTimestamp) return directTimestamp

    return getLatestChildActivityTimestamp(effectiveChildren)
  }, [lastDataTimestamp, asset, effectiveChildren])

  // 5. Query system data connections when asset is managed by a system
  const shouldFetchSystemConnections =
    stateProp === undefined &&
    hasConnection === undefined &&
    Boolean(effectiveSystemId)

  const { data: systemConnectionData, loading: systemConnectionsLoading } = useQueryWithEndpoint<
    gqlTypes.GetSystemDataConnectionsQuery,
    gqlTypes.GetSystemDataConnectionsQueryVariables
  >(
    gqlTypes.GetSystemDataConnectionsDocument,
    {
      variables: { systemId: effectiveSystemId || "" },
      skip: !shouldFetchSystemConnections,
      fetchPolicy: "cache-first",
    },
    "primary",
  )

  // 6. Query individual asset connections if not system-managed and not already active/parent
  const isDirectlyActive = asset?.monitoring_status?.toLowerCase() === "active"
  const shouldFetchConnections =
    stateProp === undefined &&
    hasConnection === undefined &&
    Boolean(asset?.id) &&
    !isManagedBySystem &&
    !isParent &&
    !isDirectlyActive

  const { data: connectionData, loading: connectionsLoading } = useQueryWithEndpoint<
    gqlTypes.GetAssetDataConnectionsQuery,
    gqlTypes.GetAssetDataConnectionsQueryVariables
  >(
    gqlTypes.GetAssetDataConnectionsDocument,
    {
      variables: { assetId: asset?.id || "" },
      skip: !shouldFetchConnections,
      fetchPolicy: "cache-first",
    },
    "primary",
  )

  // 7. Loading coordination
  const isLoading =
    loadingProp ??
    Boolean(
      (shouldFetchSystems && systemsLoading && !systemsData) ||
      (shouldFetchChildren && childrenLoading && !childrenData) ||
      (shouldFetchConnections && connectionsLoading && !connectionData) ||
      (shouldFetchSystemConnections && systemConnectionsLoading && !systemConnectionData)
    )

  // 8. Derive connection state
  let state =
    stateProp ?? resolveDataConnectionState(effectiveAsset, hasConnection, timestamp)

  // System connections take precedence if system-managed
  if (
    stateProp === undefined &&
    hasConnection === undefined &&
    shouldFetchSystemConnections &&
    systemConnectionData?.getSystemDataConnections !== undefined
  ) {
    state = resolveStateFromConnections(systemConnectionData.getSystemDataConnections, state)
  }

  // Individual asset connections determine state if fetched and not already active
  if (
    stateProp === undefined &&
    hasConnection === undefined &&
    shouldFetchConnections &&
    connectionData?.getAssetDataConnections !== undefined
  ) {
    if (state !== DataConnectionState.Active) {
      state = resolveStateFromConnections(connectionData.getAssetDataConnections, state)
    }
  }

  // 9. Ownership
  const ownership: ConnectionOwnership = isManagedBySystem
    ? "system"
    : asset?.id
      ? "asset"
      : null

  // 10. Navigation target URL
  const targetUrl =
    setupUrl ||
    (effectiveSystemId
      ? `/systems/${effectiveSystemId}/settings?tab=connections`
      : asset?.id
        ? `/assets/${asset.id}/settings?tab=connections`
        : "#")

  const relativeTimeLong = formatConnectionRelativeTime(timestamp, false)
  const relativeTimeShort = formatConnectionRelativeTime(timestamp, true)

  return {
    state,
    ownership,
    timestamp,
    isLoading,
    targetUrl,
    isManagedBySystem,
    systemId: effectiveSystemId,
    systemName: effectiveSystemName,
    relativeTimeLong,
    relativeTimeShort,
  }
}
